import {
  action,
  makeObservable,
  observable,
  computed,
  reaction,
  IReactionDisposer,
  runInAction,
} from 'mobx';
import { FilterParam, FilterSearchGroups } from './Filter';
import {
  FilterSearchParamsController,
  FilterSearchParamsControllerOptions,
} from './FilterSearchParamsController';

type Direction = '-';

export interface QueryOrderingValue<K extends string> {
  field: K;
  direction?: Direction;
}

export interface IQueryParams<K extends string> {
  filter: FilterParam[][];
  order?: QueryOrderingValue<K>[];
  search?: string;
  limit?: number;
  page: number;
  pageSize: number;
  offset?: number;
}

export interface QueryOptions<K extends string> {
  filter: FilterSearchParamsControllerOptions;
  order?: QueryOrderingValue<K>[];
  search?: string;
  page: number;
  pageSize: number;
  isLimitOffsetPagination?: boolean;
}

export class Query<K extends string = string> {
  private _disposer?: IReactionDisposer;
  private _initialValue: QueryOptions<K>;

  @observable private _page: number;
  @observable private _pageSize: number;
  @observable private _search?: string;
  @observable private _order: QueryOrderingValue<K>[];

  readonly filter: FilterSearchParamsController;

  constructor(initialValue: QueryOptions<K>) {
    this.filter = new FilterSearchParamsController(initialValue.filter);

    this._page = initialValue.page || 1;
    this._pageSize = initialValue.pageSize;
    this._order = initialValue.order ?? [];
    this._search = initialValue.search;
    this._initialValue = initialValue;

    makeObservable<Query<K>>(this);
  }

  @computed get order() {
    return this._order;
  }

  @computed get search() {
    return this._search;
  }

  @computed get pageSize() {
    return this._pageSize;
  }

  @computed get page() {
    return this._page;
  }

  @computed get value(): IQueryParams<K> {
    return {
      filter: this.filter.value,
      search: this._search,
      order: this._order,
      page: this._page,
      pageSize: this._pageSize,
    };
  }

  @computed get searchParams(): URLSearchParams {
    const filterSearchParams = new FilterSearchGroups(
      this.filter.value,
    ).toString();

    const result = new URLSearchParams(filterSearchParams);

    if (this._search) result.set('_search', this._search);

    const order = this._order?.map((v) => this._serializeOrder(v)).join(',');

    if (order) result.set('_order', order);

    if (this._initialValue.isLimitOffsetPagination) {
      result.set('_offset', (this._page - 1) * this._pageSize + '');
      result.set('_limit', this._pageSize + '');
    } else {
      result.set('_page', this._page + '');
      result.set('_page_size', this._pageSize + '');
    }

    return result;
  }

  @action setSearch = (value?: string) => {
    this._search = value;
  };

  @action setOrder = (value: QueryOrderingValue<K>[]) => {
    this._order = value;
  };

  @action addOrder = (field: K, direction: Direction) => {
    this._order = [...this._order, { field, direction: direction }];
  };

  @action setPage = (value: number) => {
    this._page = value;
  };

  @action setPageSize = (value: number) => {
    this._pageSize = value;
  };

  @action resetSearch = () => {
    this._search = this._initialValue?.search;
  };

  @action resetOrder = () => {
    this._order = this._initialValue?.order ?? [];
  };

  @action resetPage = () => {
    this._page = this._initialValue?.page ?? 1;
  };

  @action resetPageSize = () => {
    this._page = this._initialValue?.pageSize ?? 10;
  };

  @action resetAll = () => {
    runInAction(() => {
      this.resetPage();
      this.resetPageSize();
      this.resetSearch();
      this.filter.reset();
    });
  };

  private _serializeOrder = ({
    field,
    direction,
  }: QueryOrderingValue<K>): string => {
    return `${direction ?? ''}${field}`;
  };

  bind = () => {
    this._disposer = reaction(
      () => ({
        search: this.search,
        pageSize: this._pageSize,
        filterFields: this.filter.value,
      }),
      () => {
        this.setPage(1);
      },
    );

    return this.unbind;
  };

  unbind = () => {
    this._disposer?.();
    this._disposer = undefined;
  };
}
